import { http } from '@/core/api/http'
import { REAUTH_HEADER } from '@/core/api/reauth'

// ADR-072: Hệ thống › Cài đặt. Lưu cần SystemSettings.Manage + nhập lại mật khẩu (interceptor ADR-070 tự hỏi).
export interface SystemSettings {
  general: { organizationName: string; supportEmail: string | null; supportPhone: string | null; defaultLanguage?: 'vi' | 'en' | null }
  signIn: { failureLimit: number; windowMinutes: number }
  password: { minLength: number }
  session: { accessTokenMinutes: number }
  audit: { retentionDays: number }
}
export interface SystemSettingsView {
  settings: SystemSettings; defaults: SystemSettings; customized: boolean
  rowVersion: string | null; updatedBy: string | null; updatedAtUtc: string | null
}
export interface PublicSystemSettings {
  organizationName: string; supportEmail: string | null; supportPhone: string | null; passwordMinLength: number; defaultLanguage?: 'vi' | 'en'
  qOneDocumentWebUrl?: string | null
}

// ADR-082: nhóm cài đặt thay tham số Docker. Secret: null = giữ giá trị hiện có, '' = xóa, chuỗi = thay. Server không trả secret.
export interface SmtpAccount {
  host: string | null; port: number; enableSsl: boolean; userName: string | null; password: string | null
  fromAddress: string | null; fromName: string | null
}
export interface EmailSettings {
  enabled: boolean; smtp: SmtpAccount; notificationsEnabled: boolean; passwordResetEnabled: boolean; payrollEnabled: boolean
  payrollUseSeparateAccount: boolean; payroll: SmtpAccount | null
}
export interface CalendarProviderSettings { enabled: boolean; clientId: string | null; clientSecret: string | null; tenant?: string | null }
export interface CalendarIntegrationSettings { google: CalendarProviderSettings; microsoft: CalendarProviderSettings }
export interface WebPushSettings { enabled: boolean; subject: string | null; publicKey: string | null; privateKey: string | null }
// ADR-086: documentStorageMode — Local (chỉ lưu cục bộ) hoặc QOneDocument (đồng bộ hồ sơ dự án lên Q One Document).
export type DocumentStorageMode = 'Local' | 'QOneDocument'
/** ADR-086: kết nối Q One Document khi đồng bộ hồ sơ dự án; apiKey là secret (null = giữ, '' = xóa). */
export interface QOneDocumentConnection { apiBaseUrl: string | null; apiKey: string | null; webBaseUrl: string | null }
export interface PlatformSettings {
  publicBaseUrl: string | null; features: { projectIssueWorkflow: boolean; projectCancellation: boolean; documentStorageMode?: DocumentStorageMode | null }
  qoneDocument?: QOneDocumentConnection | null
}
/** BigUpdate D15: đăng nhập bằng Q One Platform; serviceClientSecret là secret. */
export interface PlatformLoginSettings {
  enabled: boolean; authority: string | null; clientId: string | null; serviceClientId: string | null; serviceClientSecret: string | null; delegateViaPlatform: boolean
}
export interface EcosystemCheck { key: string; ok: boolean; detail: string | null }
// ADR-089: hiển thị/lưu giữ thông báo và nhịp email.
export type EmailCadence = 'Immediate' | 'Hourly' | 'Daily'
export interface NotificationDeliverySettings {
  legacyAudienceDays: number; readRetentionDays: number; deliveryLogRetentionDays: number; emailDefaultCadence: EmailCadence
  dailyDigestHour: number; emailDailyLimit: number; quietHoursEnabled: boolean; quietHoursStart: string | null; quietHoursEnd: string | null; timeZone: string | null
}
export type IntegrationSection = 'email' | 'calendar' | 'webpush' | 'platform' | 'notifications' | 'ecosystem'
export interface ManagedSectionView<T> {
  settings: T; stored: boolean; locked: boolean; secretsSet: string[]; secretsUnreadable: string[]
  rowVersion: string | null; updatedBy: string | null; updatedAtUtc: string | null
}
export interface ManagedSettingsView {
  email: ManagedSectionView<EmailSettings>; calendar: ManagedSectionView<CalendarIntegrationSettings>
  webPush: ManagedSectionView<WebPushSettings>; platform: ManagedSectionView<PlatformSettings>
  redirectUris: { google: string; microsoft: string }
  notifications: ManagedSectionView<NotificationDeliverySettings>
  ecosystem: ManagedSectionView<PlatformLoginSettings>
}

export const systemSettingsApi = {
  get: () => http.get<SystemSettingsView>('/system-settings'),
  update: (settings: SystemSettings, rowVersion: string | null, reason: string) =>
    http.put<SystemSettingsView>('/system-settings', { settings, rowVersion, reason }),
  public: () => http.get<PublicSystemSettings>('/system-settings/public'),
  integrations: () => http.get<ManagedSettingsView>('/system-settings/integrations'),
  /** reauthPassword: đã hỏi trước (lưu nhiều nhóm một lần) — không hỏi lại cho từng nhóm. */
  updateIntegration: <T>(section: IntegrationSection, settings: T, rowVersion: string | null, reason: string, reauthPassword?: string) =>
    http.put<ManagedSettingsView>(`/system-settings/integrations/${section}`, { settings, rowVersion, reason },
      reauthPassword ? { headers: { [REAUTH_HEADER]: reauthPassword } } : undefined),
  resetIntegration: (section: IntegrationSection, rowVersion: string | null, reason: string) =>
    http.post<ManagedSettingsView>(`/system-settings/integrations/${section}/reset`, { rowVersion, reason }),
  sendTestEmail: (to: string, payroll = false) => http.post<void>('/system-settings/integrations/email/test', { to, payroll }),
  testEcosystem: () => http.post<{ checks: EcosystemCheck[] }>('/system-settings/integrations/ecosystem/test')
}
